import { useMemo, useState } from "react";
import { LoaderCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { submitLead } from "@/services/api";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  address: "",
  message: "",
  service_interest: "Solar Installation",
};

export function LeadForm({
  title,
  description,
  leadType,
  pageSource,
  buttonLabel,
  testIdPrefix,
  serviceOptions = ["Solar Installation", "Roofing Services", "Battery Storage", "Inspection"],
  showAddress = true,
  showMessage = true,
}) {
  const [formState, setFormState] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitLabel = useMemo(() => (isSubmitting ? "Sending..." : buttonLabel), [buttonLabel, isSubmitting]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await submitLead({
        lead_type: leadType,
        page_source: pageSource,
        name: formState.name,
        email: formState.email,
        phone: formState.phone,
        address: showAddress ? formState.address : undefined,
        message: showMessage ? formState.message : undefined,
        service_interest: formState.service_interest,
      });

      toast.success(response.message);
      setFormState(EMPTY_FORM);
    } catch (error) {
      console.error("Lead submission failed", error);
      toast.error("We couldn't send your request. Please call us and we'll help right away.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="glass-panel border-white/60 bg-white/90 shadow-[0_24px_80px_rgba(16,24,40,0.08)]" data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="space-y-3">
        <CardTitle className="text-3xl font-bold text-foreground" data-testid={`${testIdPrefix}-title`}>
          {title}
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-description`}>
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 md:grid-cols-2" data-testid={`${testIdPrefix}-form`} onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground" data-testid={`${testIdPrefix}-name-label`} htmlFor={`${testIdPrefix}-name`}>
              Full name
            </label>
            <Input data-testid={`${testIdPrefix}-name-input`} id={`${testIdPrefix}-name`} name="name" onChange={handleChange} placeholder="Your full name" required value={formState.name} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground" data-testid={`${testIdPrefix}-email-label`} htmlFor={`${testIdPrefix}-email`}>
              Email address
            </label>
            <Input data-testid={`${testIdPrefix}-email-input`} id={`${testIdPrefix}-email`} name="email" onChange={handleChange} placeholder="you@example.com" required type="email" value={formState.email} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground" data-testid={`${testIdPrefix}-phone-label`} htmlFor={`${testIdPrefix}-phone`}>
              Phone number
            </label>
            <Input data-testid={`${testIdPrefix}-phone-input`} id={`${testIdPrefix}-phone`} name="phone" onChange={handleChange} placeholder="(800) 281-2717" required value={formState.phone} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground" data-testid={`${testIdPrefix}-service-label`} htmlFor={`${testIdPrefix}-service`}>
              Service interest
            </label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid={`${testIdPrefix}-service-select`} id={`${testIdPrefix}-service`} name="service_interest" onChange={handleChange} value={formState.service_interest}>
              {serviceOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {showAddress && (
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-semibold text-foreground" data-testid={`${testIdPrefix}-address-label`} htmlFor={`${testIdPrefix}-address`}>
                Property address
              </label>
              <Input data-testid={`${testIdPrefix}-address-input`} id={`${testIdPrefix}-address`} name="address" onChange={handleChange} placeholder="Project address" value={formState.address} />
            </div>
          )}

          {showMessage && (
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-semibold text-foreground" data-testid={`${testIdPrefix}-message-label`} htmlFor={`${testIdPrefix}-message`}>
                Tell us about your project
              </label>
              <Textarea data-testid={`${testIdPrefix}-message-input`} id={`${testIdPrefix}-message`} name="message" onChange={handleChange} placeholder="Share your goals, timeline, or roof/solar concerns" required={leadType === "contact"} rows={5} value={formState.message} />
            </div>
          )}

          <div className="md:col-span-2 flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground" data-testid={`${testIdPrefix}-privacy-note`}>
              Honest follow-up only. No spam.
            </p>
            <Button className="rounded-full px-6 py-6 text-sm" data-testid={`${testIdPrefix}-submit-button`} disabled={isSubmitting} type="submit">
              {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}